import React from 'react';
import {
  AlertDialog,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useBreakpointValue,
} from '@chakra-ui/react';
import styled from '@emotion/styled';

type DialogProps = {
  isOpen: boolean;
  titleContent?: React.ReactNode;
  okText?: string;
  closeText: string;
  onClose: () => void;
  onOk?: () => void;
};

const StyledAlertDialogFooter = styled(AlertDialogFooter)`
  height: 55px;
  padding: 1px 0 0;
  justify-content: stretch;
  align-items: stretch;
  background: ${({ theme }) => theme.colors.asphalt[6]};
  border-radius: 0 0 8px 8px;
  column-gap: 1px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    height: auto;
    padding: 22px 0 0;
    justify-content: center;
    align-items: flex-start;
    background: none;
    column-gap: 20px;
  }
`;

const StyledButton = styled(Button)`
  font-weight: 400;
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.highlight};
  padding: 0;
  border-width: 0;

  &:focus {
    box-shadow: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: auto;
    height: 32px;
    line-height: 32px;
    color: ${({ theme }) => theme.colors.graphite[1]};
    padding: 0 24px;
    border-width: 1px;
  }
`;

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  titleContent,
  okText,
  closeText,
  onClose,
  onOk,
}) => {
  const cancelRef = React.useRef(null);
  const buttonVariant = useBreakpointValue(
    {
      base: 'ghost',
      md: 'outline',
    },
    'outline',
  );

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay backdropFilter={{ base: 'none', md: 'blur(2px)' }}>
        <AlertDialogContent
          borderRadius="8px"
          w={{ base: '230px', md: '392px' }}
          h={{ base: 'auto', md: '234px' }}
        >
          {buttonVariant === 'outline' && <AlertDialogCloseButton />}
          <AlertDialogHeader
            fontSize="md"
            fontWeight="600"
            textAlign="center"
            pt={{ base: '15px', md: '79px' }}
            pb={{ base: '15px', md: '0' }}
          >
            {titleContent}
          </AlertDialogHeader>

          <StyledAlertDialogFooter>
            <StyledButton
              ref={cancelRef}
              onClick={onClose}
              variant={buttonVariant}
              borderRadius={{ base: '0 0 0 8px', md: '16px' }}
              size="sm"
            >
              {closeText}
            </StyledButton>
            {okText && (
              <StyledButton
                onClick={onOk}
                variant={buttonVariant}
                borderRadius={{ base: '0 0 8px 0', md: '16px' }}
                size="sm"
              >
                {okText}
              </StyledButton>
            )}
          </StyledAlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
